import React from 'react';
import { useField } from 'formik';
import { normalizeFormikError, WrappedBaseInput, WrappedBaseInputProps } from './BaseInput';

export type ConnectedSelectInputProps = {
  /** name of the field in the formik state */
  name: string;
  /** is the field disabled */
  disabled?: boolean;
  /** list of options available for the user to select from */
  options: WrappedBaseInputProps['inputProps']['options'];
  /** label for the input */
  label?: string;
  /** help text to display below the input */
  helpText?: string;
  /** help text to display above the input */
  helpTextAbove?: string;
  /** props passed directly through to the underlying input, you should not need this in most cases */
  inputProps?: Omit<
    WrappedBaseInputProps['inputProps'],
    'name' | 'invalid' | 'onChange' | 'onBlur' | 'value' | 'type' | 'id'
  >;
  /** props passed directly through to the <FieldWrapper /> component, you should not need this in most cases */
  wrapperProps?: Omit<
    WrappedBaseInputProps['wrapperProps'],
    'id' | 'label' | 'helpText' | 'disabled' | 'validation' | 'tooltip' | 'helpTextAbove'
  >;
  /** tooltip to display next to the label */
  tooltip?: React.ReactNode;
  /** handler for when the select value changes
   * note that you should not be using this to set the value of the select input since
   * it is already being set into formik state automatically for you
   * this is only for side effects that you want to trigger when the value changes
   * like fetching dependent data based on the selected value
   */
  onChange?: (newValue: string) => void;
  /** requires the field to be touched before it shows a validation error, defaults to false */
  validationRequiresTouched?: boolean;
};

export const SelectInput = ({
  name,
  options,
  disabled,
  label,
  helpText,
  helpTextAbove,
  tooltip,
  inputProps = {},
  wrapperProps = {},
  onChange: onChangeProp,
  validationRequiresTouched = false,
}: ConnectedSelectInputProps) => {
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur } = input;
  const { error, touched } = meta;

  const hasError = !!error;
  const isTouched = !!touched;
  const isInvalid = !validationRequiresTouched ? hasError : hasError && isTouched;
  const fieldValidation = isInvalid ? { invalid: true } : {};
  const wrapperValidation = isInvalid ? { validation: { valid: !hasError, message: normalizeFormikError(error) } } : {};

  const onChange = (selectedValue: string) => {
    helpers.setValue(selectedValue);
    onChangeProp?.(selectedValue);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  return (
    <WrappedBaseInput
      wrapperProps={{
        ...wrapperProps,
        ...wrapperValidation,
        label,
        helpText,
        id: name,
        disabled,
        tooltip,
        helpTextAbove,
      }}
      inputProps={{
        ...inputProps,
        ...fieldValidation,
        value: inputValue,
        options,
        name,
        id: name,
        onChange,
        onBlur,
        disabled,
        type: 'select',
      }}
    />
  );
};
